import { Edit, Plus, Trash2 } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ApiError } from "@/api/client";
import type {
  ParentChildCategoryViewFilterInput,
  ParentChildCategoryViewNode,
} from "@/api/models/parentChildCategoryView";
import type { ParentChildCategoryViewSortInput } from "@/api/services/parentChildCategoryGraphqlService";
import {
  getParentCategoriesGraphql,
  getParentChildCategoriesCount,
  getParentChildCategoriesGraphql,
  getParentChildCategoryDistinctValues,
} from "@/api/services/parentChildCategoryGraphqlService";
import {
  createSubcategory,
  deleteSubcategory,
  updateSubcategory,
} from "@/api/services/transactionCategoryService";
import {
  type ColumnConfig,
  type ColumnFilters,
  MaximusTableReusable,
} from "@/components/MaximusTableReusable";
import { SCREEN_NAMES } from "@/constants/screenNames";
import { useApp } from "@/contexts/AppContext";
import { useExportToExcel } from "@/hooks/useExportToExcel";
import { usePermissions } from "@/hooks/usePermissions";
import {
  type DefaultColumn,
  useScreenColumnPreferences,
} from "@/hooks/useScreenColumnPreferences";
import { resolveExportColumns } from "@/lib/exportColumns";
import type { TransactionCategory } from "@/types";
import type { CategorySubcategoryMapping } from "../../types/categoryMapping";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { CategoryMappingRuleModal } from "./CategoryMappingRuleModal";

const INITIAL_DEMO_MAPPINGS: CategorySubcategoryMapping[] = [
  {
    id: "1",
    parentCategory: "Materials",
    subcategoryName: "Construction Materials",
    description: "Building materials, concrete, timber, bricks",
    isActive: true,
    createdAt: new Date("2024-01-01"),
    updatedAt: new Date("2024-01-01"),
  },
  {
    id: "2",
    parentCategory: "Materials",
    subcategoryName: "Small Tools & Consumables",
    description: "Hand tools, fasteners, adhesives, consumables",
    isActive: true,
    createdAt: new Date("2024-01-01"),
    updatedAt: new Date("2024-01-01"),
  },
  {
    id: "3",
    parentCategory: "Payroll",
    subcategoryName: "Site Workers",
    description: "Construction site staff wages",
    isActive: true,
    createdAt: new Date("2024-01-01"),
    updatedAt: new Date("2024-01-01"),
  },
  {
    id: "4",
    parentCategory: "Payroll",
    subcategoryName: "Office Staff",
    description: "Administrative and management staff",
    isActive: true,
    createdAt: new Date("2024-01-01"),
    updatedAt: new Date("2024-01-01"),
  },
  {
    id: "5",
    parentCategory: "Equipment",
    subcategoryName: "Plant Hire",
    description: "Heavy machinery and equipment rental",
    isActive: true,
    createdAt: new Date("2024-01-01"),
    updatedAt: new Date("2024-01-01"),
  },
  {
    id: "6",
    parentCategory: "Subcontractors",
    subcategoryName: "Electrical",
    description: "Electrical subcontractor work",
    isActive: true,
    createdAt: new Date("2024-01-01"),
    updatedAt: new Date("2024-01-01"),
  },
  {
    id: "7",
    parentCategory: "Subcontractors",
    subcategoryName: "Plumbing",
    description: "Plumbing subcontractor work",
    isActive: true,
    createdAt: new Date("2024-01-01"),
    updatedAt: new Date("2024-01-01"),
  },
];

const SORT_FIELD_MAP: Record<string, string> = {
  parentCategory: "ParentCategory",
  subcategoryName: "SubcategoryName",
  description: "Description",
  isActive: "IsActive",
};

const FILTER_FIELD_MAP: Record<string, string> = {
  parentCategory: "parentCategory",
  subcategoryName: "subcategoryName",
  description: "description",
  isActive: "isActive",
};

const TEXT_FILTER_FIELDS = new Set([
  "parentCategory",
  "subcategoryName",
  "description",
]);

const EXPORT_COLUMN_MAP: Record<string, { name: string; displayName: string }> =
  {
    parentCategory: { name: "parentcategory", displayName: "Parent Category" },
    subcategoryName: { name: "subcategoryname", displayName: "Child Category" },
    description: { name: "description", displayName: "Description" },
    isActive: { name: "isactive", displayName: "Status" },
  };

function buildApiColumnFilter(
  columnFilters: ColumnFilters,
): ParentChildCategoryViewFilterInput | undefined {
  const entries = Object.entries(columnFilters);
  if (entries.length === 0) return undefined;

  const filterInput: ParentChildCategoryViewFilterInput = {};

  for (const [columnKey, filterValue] of entries) {
    const apiField = FILTER_FIELD_MAP[columnKey];
    if (!apiField) continue;

    if (TEXT_FILTER_FIELDS.has(apiField)) {
      if (filterValue.mode === "isEmpty") {
        (filterInput as Record<string, unknown>)[apiField] = { isBlank: true };
      } else if (filterValue.value != null && filterValue.value !== "") {
        const strValue = String(filterValue.value);
        if (filterValue.mode === "equals") {
          (filterInput as Record<string, unknown>)[apiField] = { eq: strValue };
        } else {
          (filterInput as Record<string, unknown>)[apiField] = {
            contains: strValue,
          };
        }
      }
    } else if (apiField === "isActive") {
      if (filterValue.value === "Active" || filterValue.value === true) {
        filterInput.isActive = { eq: true };
      } else if (
        filterValue.value === "Inactive" ||
        filterValue.value === false
      ) {
        filterInput.isActive = { eq: false };
      }
    }
  }

  return Object.keys(filterInput).length > 0 ? filterInput : undefined;
}

const DEFAULT_CATEGORY_COLUMNS: DefaultColumn[] = [
  { key: "parentCategory", defaultVisible: true, defaultOrder: 0 },
  { key: "subcategoryName", defaultVisible: true, defaultOrder: 1 },
  { key: "description", defaultVisible: true, defaultOrder: 2 },
  { key: "isActive", defaultVisible: true, defaultOrder: 3 },
  { key: "actions", defaultVisible: true, defaultOrder: 4 },
];

export const CategoryMappingPage: React.FC = () => {
  const { mode } = useApp();
  const { hasPermission } = usePermissions();
  const isLive = mode === "live";

  const {
    visibleColumns: savedVisibleColumns,
    savePreferences: saveColumnPreferences,
  } = useScreenColumnPreferences(
    SCREEN_NAMES.PARENT_CHILD_CATEGORIES,
    DEFAULT_CATEGORY_COLUMNS,
  );

  const [mappings, setMappings] = useState<CategorySubcategoryMapping[]>(
    INITIAL_DEMO_MAPPINGS,
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMapping, setEditingMapping] =
    useState<CategorySubcategoryMapping | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<string | null>(null);

  // Live-mode state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiParentCategories, setApiParentCategories] = useState<
    { id: number; name: string }[]
  >([]);

  // Server-side sort state (live mode)
  const [sortParams, setSortParams] = useState<
    ParentChildCategoryViewSortInput[] | undefined
  >([{ field: "ParentCategory", order: "Ascending" }]);

  // Server-side pagination (live mode)
  const [serverPage, setServerPage] = useState(1);
  const [serverPageSize, setServerPageSize] = useState(25);
  const [pageCursors, setPageCursors] = useState<Record<number, string>>({});
  const [totalCount, setTotalCount] = useState(0);

  // Server-side parent category options for column filter (live mode)
  const [parentCategoryOptions, setParentCategoryOptions] = useState<string[]>(
    [],
  );

  // Server-side search (live mode)
  const [serverSearchTerm, setServerSearchTerm] = useState<string>("");

  // Column-level filters from AdvancedFilterPanel
  const [columnFilterParams, setColumnFilterParams] = useState<
    ParentChildCategoryViewFilterInput | undefined
  >(undefined);

  // Raw column filters for demo-mode client-side filtering
  const [demoColumnFilters, setDemoColumnFilters] = useState<ColumnFilters>({});

  // Build server-side filter combining search and column filters
  const buildServerFilter = useCallback(():
    | ParentChildCategoryViewFilterInput
    | undefined => {
    const filter: ParentChildCategoryViewFilterInput = {};

    if (serverSearchTerm.trim()) {
      filter.summary = serverSearchTerm.trim();
    }

    // Merge column-level filters
    if (columnFilterParams) {
      Object.assign(filter, columnFilterParams);
    }

    return Object.keys(filter).length > 0 ? filter : undefined;
  }, [serverSearchTerm, columnFilterParams]);

  // Fetch data via GraphQL (live mode)
  const fetchGraphqlData = useCallback(
    async (
      sort?: ParentChildCategoryViewSortInput[],
      first?: number,
      after?: string,
      filter?: ParentChildCategoryViewFilterInput,
    ) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await getParentChildCategoriesGraphql(
          filter,
          first ?? serverPageSize,
          after,
          sort,
        );

        const mapped: CategorySubcategoryMapping[] = result.edges.map(
          (edge) => {
            const node: ParentChildCategoryViewNode = edge.node;
            return {
              id: String(node.id),
              parentCategory: node.parentCategory as TransactionCategory,
              parentCategoryId: node.parentCategoryId,
              subcategoryName: node.subcategoryName,
              description: node.description ?? undefined,
              isActive: node.isActive,
              createdAt: new Date(),
              updatedAt: new Date(),
            };
          },
        );
        setMappings(mapped);
        return result.pageInfo;
      } catch (err) {
        const msg =
          err instanceof ApiError ? err.message : "Failed to fetch categories";
        setError(msg);
        setMappings([]);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [serverPageSize],
  );

  // Fetch parent categories from the new view (live mode)
  const fetchParentCategories = useCallback(async () => {
    try {
      const result = await getParentCategoriesGraphql(500);
      setApiParentCategories(
        result.edges.map((edge) => ({
          id: edge.node.id,
          name: edge.node.name,
        })),
      );
    } catch (err) {
      console.error("Failed to fetch parent categories:", err);
    }
  }, []);

  // Fetch distinct parent category values for column filter (live mode)
  const fetchParentCategoryOptions = useCallback(async () => {
    try {
      const values =
        await getParentChildCategoryDistinctValues("ParentCategory");
      setParentCategoryOptions(values);
    } catch (err) {
      console.error("Failed to fetch parent category options:", err);
    }
  }, []);

  // Fetch total count (live mode)
  const fetchTotalCount = useCallback(
    async (filter?: ParentChildCategoryViewFilterInput) => {
      try {
        const count = await getParentChildCategoriesCount(filter);
        setTotalCount(count);
      } catch (err) {
        console.error("Failed to fetch count:", err);
      }
    },
    [],
  );

  // Main data fetch effect (live mode)
  useEffect(() => {
    if (!isLive) return;
    const filter = buildServerFilter();
    const cursor = serverPage === 1 ? undefined : pageCursors[serverPage];
    const currentFetchPage = serverPage;
    fetchGraphqlData(sortParams, serverPageSize, cursor, filter).then(
      (pageInfo) => {
        if (pageInfo?.endCursor) {
          setPageCursors((prev) => ({
            ...prev,
            [currentFetchPage + 1]: pageInfo.endCursor as string,
          }));
        }
      },
    );
    fetchTotalCount(filter);
  }, [
    isLive,
    sortParams,
    serverPage,
    serverPageSize,
    serverSearchTerm,
    columnFilterParams,
    buildServerFilter,
    fetchGraphqlData,
    fetchTotalCount,
    pageCursors,
  ]);

  // Fetch parent categories and filter options on mount (live mode)
  useEffect(() => {
    if (isLive) {
      fetchParentCategories();
      fetchParentCategoryOptions();
    }
  }, [isLive, fetchParentCategories, fetchParentCategoryOptions]);

  // Reset to demo data when switching to demo mode
  useEffect(() => {
    if (!isLive) {
      setMappings(INITIAL_DEMO_MAPPINGS);
      setApiParentCategories([]);
      setError(null);
    }
  }, [isLive]);

  // Refresh data after CRUD operations (live mode)
  const refreshData = useCallback(async () => {
    const filter = buildServerFilter();
    const cursor = serverPage === 1 ? undefined : pageCursors[serverPage];
    await fetchGraphqlData(sortParams, serverPageSize, cursor, filter);
    await fetchTotalCount(filter);
    await fetchParentCategoryOptions();
    await fetchParentCategories();
  }, [
    buildServerFilter,
    serverPage,
    pageCursors,
    sortParams,
    serverPageSize,
    fetchGraphqlData,
    fetchTotalCount,
    fetchParentCategoryOptions,
    fetchParentCategories,
  ]);

  // Get unique parent categories for column filter options
  const uniqueParentCategories = useMemo(() => {
    if (isLive) return parentCategoryOptions;
    const categories = new Set(mappings.map((m) => m.parentCategory));
    return Array.from(categories).sort();
  }, [isLive, parentCategoryOptions, mappings]);

  // Handle server-side search
  const handleSearchChange = useCallback(
    (query: string) => {
      setServerSearchTerm(query);
      if (isLive) {
        setServerPage(1);
        setPageCursors({});
      }
    },
    [isLive],
  );

  // Handle server-side sort
  const handleSortChange = useCallback(
    (column: string | null, direction: "asc" | "desc" | null) => {
      if (!column || !direction) {
        setSortParams(undefined);
      } else {
        const field = SORT_FIELD_MAP[column];
        if (!field) {
          setSortParams(undefined);
        } else {
          setSortParams([
            { field, order: direction === "asc" ? "Ascending" : "Descending" },
          ]);
        }
      }
      setServerPage(1);
      setPageCursors({});
    },
    [],
  );

  // Handle column-level filter changes from AdvancedFilterPanel
  const handleColumnFilterChange = useCallback(
    (filters: ColumnFilters) => {
      if (isLive) {
        const apiFilter = buildApiColumnFilter(filters);
        setColumnFilterParams(apiFilter);
        setServerPage(1);
        setPageCursors({});
      } else {
        setDemoColumnFilters(filters);
      }
    },
    [isLive],
  );

  // Apply column filters for demo mode
  const filteredMappings = useMemo(() => {
    if (isLive) return mappings;
    const entries = Object.entries(demoColumnFilters);
    if (entries.length === 0) return mappings;

    return mappings.filter((row) => {
      for (const [column, filter] of entries) {
        if (!filter) continue;
        const value = (row as unknown as Record<string, unknown>)[column];

        if (filter.mode === "isEmpty") {
          if (value !== null && value !== undefined && value !== "")
            return false;
          continue;
        }

        if (
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== ""
        ) {
          if (column === "isActive") {
            const expected = filter.value === "Active";
            if (value !== expected) return false;
          } else if (typeof value === "string") {
            const filterStr = String(filter.value).toLowerCase();
            const rowStr = value.toLowerCase();
            if (filter.mode === "equals") {
              if (rowStr !== filterStr) return false;
            } else {
              if (!rowStr.includes(filterStr)) return false;
            }
          }
        }
      }
      return true;
    });
  }, [isLive, mappings, demoColumnFilters]);

  // Export to Excel (same as Synced Transactions)
  // Ordered visible column keys reported by the grid when the export is triggered.
  const orderedVisibleKeysRef = useRef<string[] | null>(null);

  const getVisibleExportColumns = useCallback(
    () =>
      resolveExportColumns(orderedVisibleKeysRef.current, EXPORT_COLUMN_MAP),
    [],
  );

  const getExportFilter = useCallback(() => {
    if (!columnFilterParams) return undefined;
    return columnFilterParams as Record<string, unknown>;
  }, [columnFilterParams]);

  const { triggerExport } = useExportToExcel({
    form: "ParentChildCategories",
    getColumns: getVisibleExportColumns,
    getFilter: getExportFilter,
    fileName: "parent-child-categories-export",
    onFinished: () =>
      toast.success("Export complete", {
        description: "Your file has been downloaded.",
      }),
    onError: (msg) => toast.error("Export failed", { description: msg }),
  });

  // CRUD handlers
  const handleAddMapping = () => {
    setEditingMapping(null);
    setIsModalOpen(true);
  };

  const handleEditMapping = (mapping: CategorySubcategoryMapping) => {
    setEditingMapping(mapping);
    setIsModalOpen(true);
  };

  const handleSaveMapping = async (
    mappingData: Omit<
      CategorySubcategoryMapping,
      "id" | "createdAt" | "updatedAt"
    >,
  ) => {
    if (isLive) {
      try {
        if (editingMapping) {
          const numericId = Number(editingMapping.id);
          await updateSubcategory(numericId, {
            name: mappingData.subcategoryName,
            description: mappingData.description ?? null,
            isActive: mappingData.isActive,
            parentCategoryId: mappingData.parentCategoryId ?? null,
          });
          toast.success("Updated", {
            description: `Subcategory "${mappingData.subcategoryName}" updated successfully.`,
          });
        } else {
          if (!mappingData.parentCategoryId) return;
          await createSubcategory({
            name: mappingData.subcategoryName,
            parentCategoryId: mappingData.parentCategoryId,
            description: mappingData.description ?? null,
            isActive: mappingData.isActive,
          });
          toast.success("Created", {
            description: `Subcategory "${mappingData.subcategoryName}" created successfully.`,
          });
        }
        await refreshData();
      } catch (err) {
        const description =
          err instanceof ApiError
            ? err.message
            : "Failed to save subcategory. Please try again.";
        toast.error("Error");
      }
    } else {
      if (editingMapping) {
        setMappings(
          mappings.map((m) =>
            m.id === editingMapping.id
              ? { ...m, ...mappingData, updatedAt: new Date() }
              : m,
          ),
        );
      } else {
        const newMapping: CategorySubcategoryMapping = {
          ...mappingData,
          id: Date.now().toString(),
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        setMappings([...mappings, newMapping]);
      }
    }
  };

  const handleDeleteClick = (mappingId: string) => {
    setMappingToDelete(mappingId);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (mappingToDelete) {
      if (isLive) {
        try {
          await deleteSubcategory(Number(mappingToDelete));
          toast.success("Deleted", {
            description: "Subcategory deleted successfully.",
          });
          await refreshData();
        } catch (err) {
          const description =
            err instanceof ApiError
              ? err.message
              : "Failed to delete subcategory. Please try again.";
          toast.error("Error");
        }
      } else {
        setMappings(mappings.filter((m) => m.id !== mappingToDelete));
      }
    }
    setShowDeleteConfirm(false);
    setMappingToDelete(null);
  };

  const handleToggleActive = async (mappingId: string) => {
    if (isLive) {
      const mapping = mappings.find((m) => m.id === mappingId);
      if (!mapping) return;
      try {
        await updateSubcategory(Number(mappingId), {
          isActive: !mapping.isActive,
        });
        toast.success("Updated", {
          description: `Subcategory "${mapping.subcategoryName}" ${mapping.isActive ? "deactivated" : "activated"}.`,
        });
        await refreshData();
      } catch (err) {
        const description =
          err instanceof ApiError
            ? err.message
            : "Failed to update subcategory status.";
        toast.error("Error");
      }
    } else {
      setMappings(
        mappings.map((m) =>
          m.id === mappingId
            ? { ...m, isActive: !m.isActive, updatedAt: new Date() }
            : m,
        ),
      );
    }
  };

  // Column configuration
  const columns: ColumnConfig<CategorySubcategoryMapping>[] = useMemo(
    () => [
      {
        key: "parentCategory",
        label: "Parent Category",
        sortable: true,
        filterable: true,
        filterConfig: {
          type: "select" as const,
          options: uniqueParentCategories,
        },
        align: "left" as const,
        width: 180,
        render: (row: CategorySubcategoryMapping) => (
          <Badge variant="secondary">{row.parentCategory}</Badge>
        ),
      },
      {
        key: "subcategoryName",
        label: "Child Category",
        sortable: true,
        filterable: true,
        filterConfig: { type: "text" as const },
        align: "left" as const,
        width: 200,
        render: (row: CategorySubcategoryMapping) => (
          <span className="font-medium text-sm">{row.subcategoryName}</span>
        ),
      },
      {
        key: "description",
        label: "Description",
        sortable: true,
        filterable: true,
        filterConfig: { type: "text" as const },
        align: "left" as const,
        width: 280,
        render: (row: CategorySubcategoryMapping) => (
          <span className="text-sm text-muted-foreground">
            {row.description || "—"}
          </span>
        ),
      },
      {
        key: "isActive",
        label: "Status",
        sortable: true,
        filterable: true,
        filterConfig: {
          type: "select" as const,
          options: ["Active", "Inactive"],
        },
        align: "left" as const,
        width: 150,
        render: (row: CategorySubcategoryMapping) => (
          <div className="flex items-center gap-2">
            <Switch
              checked={row.isActive}
              onCheckedChange={() => handleToggleActive(row.id)}
            />
            <span className="text-sm text-muted-foreground">
              {row.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        ),
      },
      {
        key: "actions",
        label: "Actions",
        sortable: false,
        filterable: false,
        align: "right" as const,
        width: 100,
        render: (row: CategorySubcategoryMapping) => (
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleEditMapping(row)}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteClick(row.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [uniqueParentCategories],
  );

  const mappingToDeleteData = mappings.find((m) => m.id === mappingToDelete);

  return (
    <div className="flex flex-col h-full space-y-2 px-2 pt-0 pb-2 overflow-y-auto">
      <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        <span className="font-medium">This is an optional feature.</span> If
        your parent categories from your accounting system are already granular
        enough for cash flow analysis, you don't need to configure anything
        here. Use this area only if you want to break a parent category down
        into more specific child values — transactions can then be reassigned to
        a child category on the Transactions screen.
      </div>

      {isLive && apiParentCategories.length === 0 && !isLoading && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <span className="font-medium">
            No parent categories have been synced yet.
          </span>{" "}
          Connect a data source and enable Parent Category Source to populate
          the parent category list.
        </div>
      )}

      {isLive && error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <p className="font-medium">Failed to load categories</p>
          <p className="text-xs mt-1">{error}</p>
        </div>
      ) : (
        <div className="min-h-[400px] flex-1 flex flex-col -mt-1">
          <MaximusTableReusable
            data={filteredMappings}
            columns={columns}
            title=""
            isLoading={isLoading && isLive}
            searchPlaceholder="Search categories..."
            enableRowSelection={false}
            enableZebraStriping={true}
            showAdvancedFilter={true}
            initialVisibleColumns={savedVisibleColumns}
            onExportToExcel={
              isLive && hasPermission("export_data")
                ? (orderedVisibleKeys) => {
                    orderedVisibleKeysRef.current = orderedVisibleKeys;
                    triggerExport();
                  }
                : undefined
            }
            headerActions={
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      tabIndex={
                        isLive && apiParentCategories.length === 0
                          ? 0
                          : undefined
                      }
                    >
                      <Button
                        size="sm"
                        onClick={handleAddMapping}
                        disabled={isLive && apiParentCategories.length === 0}
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Child Category
                      </Button>
                    </span>
                  </TooltipTrigger>
                  {isLive && apiParentCategories.length === 0 && (
                    <TooltipContent>
                      <p>
                        No parent categories synced — connect a data source
                        first
                      </p>
                    </TooltipContent>
                  )}
                </Tooltip>
              </TooltipProvider>
            }
            onColumnConfigSave={(visibleKeys) => {
              const cols = visibleKeys.map((key, idx) => ({
                key,
                isVisible: true,
                order: idx,
              }));
              const allColumnKeys = columns.map((c) => c.key);
              const hiddenCols = allColumnKeys
                .filter((k) => !visibleKeys.includes(k))
                .map((key, idx) => ({
                  key,
                  isVisible: false,
                  order: visibleKeys.length + idx,
                }));
              saveColumnPreferences([...cols, ...hiddenCols]);
            }}
            onSortChange={isLive ? handleSortChange : undefined}
            onFilterChange={handleColumnFilterChange}
            onSearchChange={isLive ? handleSearchChange : undefined}
            onSearch={
              !isLive
                ? (data, query) => {
                    const q = query.toLowerCase();
                    return data.filter(
                      (m) =>
                        m.subcategoryName.toLowerCase().includes(q) ||
                        m.description?.toLowerCase().includes(q) ||
                        false ||
                        m.parentCategory.toLowerCase().includes(q),
                    );
                  }
                : undefined
            }
            pagination={{
              enabled: true,
              defaultPageSize: 25,
              pageSizeOptions: [25, 50, 100, 200],
              ...(isLive
                ? {
                    totalItems: totalCount,
                    controlledPage: serverPage,
                    onPageChange: (page: number) => {
                      setServerPage(page);
                    },
                    onPageSizeChange: (newSize: number) => {
                      setServerPageSize(newSize);
                      setServerPage(1);
                      setPageCursors({});
                    },
                  }
                : {}),
            }}
            rowIdAccessor={(row) => String(row.id)}
          />
        </div>
      )}

      <CategoryMappingRuleModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMapping}
        editingRule={editingMapping}
        parentCategories={
          isLive
            ? apiParentCategories.map((c) => ({ id: c.id, name: c.name }))
            : undefined
        }
      />

      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Child Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the subcategory "
              {mappingToDeleteData?.subcategoryName}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
